import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useNotification } from '../../hooks/useNotification';
import { useAuth } from '../../hooks/useAuth';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaArrowRight } from 'react-icons/fa';
import { BsWhatsapp } from 'react-icons/bs';
import Layout from '../../components/Layout/Layout';
import Button from '../../components/Common/Button';
import Alert from '../../components/Common/Alert';
import styles from './styles/LoginPage.module.css';
import SEO from '../../components/SEO/SEO';

export default function LoginPage() {
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        setError('');
    };

    const validateForm = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Email invalide (ex: votre@email.com)');
            return false;
        }

        if (formData.password.length < 6) {
            setError('Le mot de passe doit contenir au moins 6 caractères');
            return false;
        }

        return true;
    };

    const getErrorMessage = (errorCode) => {
        const errorMessages = {
            'auth/user-not-found': 'Cet email n\'existe pas. Veuillez créer un compte.',
            'auth/wrong-password': 'Mot de passe incorrect.',
            'auth/invalid-credential': 'Email ou mot de passe incorrect.',
            'auth/invalid-email': 'Email invalide.',
            'auth/user-disabled': 'Ce compte a été désactivé.',
            'auth/too-many-requests': 'Trop de tentatives. Réessayez plus tard.',
        };

        return errorMessages[errorCode] || `Erreur: ${errorCode}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            await login(formData.email, formData.password);
            showNotification('Connexion réussie!', 'success');

            setTimeout(() => {
                navigate('/reservation', { replace: true });
            }, 1500);
        } catch (error) {
            const errorMessage = getErrorMessage(error.code);
            setError(errorMessage);
            showNotification(errorMessage, 'error');
            setLoading(false);
        }
    };

    return (
        <>
            <SEO
                title="Connexion | OV Hairstyle"
                description="Connectez-vous à votre compte OV Hairstyle pour accéder à vos réservations."
                url="https://ovhairstyle.com/login"
                image="https://ov-hairstyle.vercel.app/images/og-image.jpeg"
            />
            <Layout>
                <div className={styles.loginContainer}>
                    {/* Éléments de décoration */}
                    <div className={styles.decoration1}></div>
                    <div className={styles.decoration2}></div>

                    <div className={styles.content}>
                        {/* Colonne gauche - Texte */}
                        <div className={styles.leftColumn}>
                            <div className={styles.leftContent}>
                                <h1 className={styles.mainTitle}>
                                    Bienvenue chez<br />
                                    <span className={styles.highlight}>OV Hairstyle</span>
                                </h1>
                                <p className={styles.description}>
                                    Connectez-vous pour accéder à vos rendez-vous et gérer votre profil
                                </p>

                                <div className={styles.features}>
                                    <div className={styles.feature}>
                                        <div className={styles.featureIcon}>✓</div>
                                        <span>Réservez facilement vos rendez-vous</span>
                                    </div>
                                    <div className={styles.feature}>
                                        <div className={styles.featureIcon}>✓</div>
                                        <span>Gérez votre historique</span>
                                    </div>
                                    <div className={styles.feature}>
                                        <div className={styles.featureIcon}>✓</div>
                                        <span>Recevez des notifications</span>
                                    </div>
                                </div>

                                <div className={styles.whatsappCTA}>
                                    <p>Besoin d'aide?</p>
                                    <a href="https://wa.me/243972315704" target="_blank" rel="noreferrer" className={styles.whatsappLink}>
                                        <BsWhatsapp /> Discuter sur WhatsApp
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Colonne droite - Formulaire */}
                        <div className={styles.rightColumn}>
                            <div className={styles.formCard}>
                                <div className={styles.formHeader}>
                                    <h2>Connexion</h2>
                                    <p>Entrez vos identifiants</p>
                                </div>

                                {error && (
                                    <Alert
                                        type="error"
                                        message={error}
                                        onClose={() => setError('')}
                                    />
                                )}

                                <form onSubmit={handleSubmit} className={styles.form}>
                                    {/* Email */}
                                    <div className={styles.formGroup}>
                                        <label htmlFor="email">Email</label>
                                        <div className={styles.inputWrapper}>
                                            <FaEnvelope className={styles.inputIcon} />
                                            <input
                                                id="email"
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="votre@email.com"
                                                required
                                                autoComplete="email"
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>

                                    {/* Mot de passe */}
                                    <div className={styles.formGroup}>
                                        <label htmlFor="password">Mot de passe</label>
                                        <div className={styles.inputWrapper}>
                                            <FaLock className={styles.inputIcon} />
                                            <input
                                                id="password"
                                                type={showPassword ? 'text' : 'password'}
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                placeholder="••••••••"
                                                required
                                                autoComplete="current-password"
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                className={styles.togglePassword}
                                                onClick={() => setShowPassword(!showPassword)}
                                                disabled={loading}
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                        <small className={styles.hint}>
                                            Minimum 6 caractères
                                        </small>
                                    </div>

                                    {/* Bouton Connexion */}
                                    <Button
                                        type="submit"
                                        size="large"
                                        loading={loading}
                                        className={styles.submitBtn}
                                        disabled={loading}
                                    >
                                        {loading ? 'Connexion en cours...' : 'Se connecter'}
                                        {!loading && <FaArrowRight className={styles.btnIcon} />}
                                    </Button>
                                </form>

                                {/* Lien inscription */}
                                <div className={styles.footer}>
                                    <p>
                                        Pas encore inscrit?{' '}
                                        <Link to="/register" className={styles.link}>
                                            Créer un compte
                                        </Link>
                                    </p>
                                </div>

                                {/* Divider */}
                                <div className={styles.divider}>
                                    <span>ou</span>
                                </div>

                                {/* Lien rapide WhatsApp */}
                                <a href="https://wa.me/243972315704" target="_blank" rel="noreferrer" className={styles.whatsappQuickLink}>
                                    <BsWhatsapp /> Réserver via WhatsApp
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    );
}
